import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Message } from '../types';
import { Brand, Colors, Fonts, Radii, Spacing } from '../constants';

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  showSender?: boolean;
}

/**
 * MessageBubble Component (design v2)
 *
 * Own messages: Bosgroen bubble, white text, Saliemist time. Other party:
 * white bubble with a hairline, ink text — it has to read against the linen
 * chat surface. System messages are a sunken chip (no border) so they sit
 * between the two voices without competing with either.
 */
export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwnMessage,
  showSender = false,
}) => {
  const isSystemMessage = message.message_type === 'system';

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // System messages have special styling. Historical payment-flow events
  // (offer/transaction system messages from before the payment flow was
  // removed) simply render their stored content text.
  if (isSystemMessage) {
    return (
      <View style={styles.systemMessageContainer}>
        <View style={styles.systemMessageBubble}>
          <Ionicons name="people-outline" size={14} color={Colors.accentStrong} />
          <Text style={styles.systemMessageText}>{message.content}</Text>
        </View>
        <Text style={styles.systemMessageTime}>{formatTime(message.created_at)}</Text>
      </View>
    );
  }

  // Regular text messages (sender vs receiver styling)
  return (
    <View style={[styles.messageContainer, isOwnMessage ? styles.ownMessageContainer : styles.otherMessageContainer]}>
      {showSender && !isOwnMessage && message.sender && (
        <Text style={styles.senderName}>
          {message.sender.first_name} {message.sender.last_name}
        </Text>
      )}
      <View style={[styles.messageBubble, isOwnMessage ? styles.ownMessageBubble : styles.otherMessageBubble]}>
        <Text style={[styles.messageText, isOwnMessage ? styles.ownMessageText : styles.otherMessageText]}>
          {message.content}
        </Text>
        <View style={styles.messageFooter}>
          <Text style={[styles.messageTime, isOwnMessage ? styles.ownMessageTime : styles.otherMessageTime]}>
            {formatTime(message.created_at)}
          </Text>
          {isOwnMessage && message.read_at && (
            <Ionicons name="checkmark-done-outline" size={13} color={Brand.saliemist} />
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // System message styles
  systemMessageContainer: {
    width: '100%',
    alignItems: 'center',
    marginVertical: 14,
    gap: Spacing.xs,
  },
  systemMessageBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Colors.card, // sunken — one step below the linen surface
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radii.control,
    maxWidth: '85%',
  },
  systemMessageText: {
    fontFamily: Fonts.medium,
    fontSize: 12,
    lineHeight: 17,
    color: Colors.textMedium,
    flexShrink: 1,
  },
  systemMessageTime: {
    fontSize: 11,
    color: Colors.textLight,
  },

  // Regular message container
  messageContainer: {
    width: '100%',
    marginVertical: 3,
    paddingHorizontal: 14,
  },
  ownMessageContainer: {
    alignItems: 'flex-end',
  },
  otherMessageContainer: {
    alignItems: 'flex-start',
  },

  // Sender name (for group chats or multi-user conversations)
  senderName: {
    fontSize: 12,
    color: Colors.textMedium,
    marginBottom: 4,
    marginLeft: 12,
  },

  // Message bubble
  messageBubble: {
    maxWidth: '76%',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: Radii.bubble,
  },
  ownMessageBubble: {
    backgroundColor: Brand.bosgroen,
    borderBottomRightRadius: Radii.tag,
  },
  otherMessageBubble: {
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderBottomLeftRadius: Radii.tag,
  },

  // Message text
  messageText: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    lineHeight: 21,
  },
  ownMessageText: {
    color: Colors.white,
  },
  otherMessageText: {
    color: Colors.text,
  },

  // Message footer (time + read indicator)
  messageFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 4,
    gap: 4,
  },
  messageTime: {
    fontSize: 11,
  },
  ownMessageTime: {
    color: Brand.saliemist,
  },
  otherMessageTime: {
    color: Colors.textLight,
  },
});
